import React, { useMemo, useState } from "react";

import { colors } from "@material-ui/core";

import { Button } from "@/design-system/components/Button/Button";
import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";
import { Checkbox } from "@/design-system/components/Checkbox/Checkbox";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";

import { LoadedDestinationInfo } from "@/types/global";
import { DestinationConfigRow } from "@/destinations/configs/shared/ConfigPageRow";

import { TermsOfServiceState } from "@/audiences";
import { Confirmation } from "@/components/icons/svg/figma/Confirmation";
import { makeRequest } from "@/util";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";

type LinkedInAudiencesTermsOfServiceRowProps = {
  existingConfig: LoadedDestinationInfo;
};

function tosStateFromSettings(
  settings: Record<string, any>,
): TermsOfServiceState {
  if (
    !settings ||
    !("termsOfServiceTestState" in settings) ||
    settings["termsOfServiceTestState"] === "tosUnknown"
  ) {
    return "tosUnknown";
  }

  if (settings["termsOfServiceTestState"] === "tosTestFailed") {
    return "tosTestFailed";
  }

  if (settings["termsOfServiceTestState"] === "tosTestSucceeded") {
    return "tosTestSucceeded";
  }

  return "tosUnknown";
}

export const LinkedInAudiencesTermsOfServiceRow = ({
  existingConfig,
}: LinkedInAudiencesTermsOfServiceRowProps) => {
  const settings = existingConfig.settings || {};
  const adAccountIdSet = "adAccountId" in settings && settings["adAccountId"];
  const tosState = tosStateFromSettings(settings);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();
  const [didOpenTOSLink, setDidOpenTOSLink] = useState(false);

  const handleOpenTOSLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.persist();
    e.preventDefault();
    e.stopPropagation();

    setDidOpenTOSLink(true);

    window.open(
      "https://www.linkedin.com/help/lms/answer/a1320991",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const errorMessage: React.ReactNode | null = useMemo(() => {
    if (error) {
      return error;
    }

    if (!didOpenTOSLink) {
      return (
        <>
          You must open and view the{" "}
          <a
            href="https://www.linkedin.com/help/lms/answer/a1320991"
            target="_blank"
            rel="noopener noreferrer"
            className="text-textActionSelected underline"
            onClick={handleOpenTOSLink}
          >
            LinkedIn terms of service
          </a>{" "}
          before you can check the box and click "Accept".
        </>
      );
    }

    return null;
  }, [error, didOpenTOSLink]);

  const textColor = tosState === "tosTestFailed" ? colors.red[500] : undefined;

  const labelText = useMemo(() => {
    switch (tosState) {
      case "tosTestFailed":
        return "LinkedIn's Healthcare Ads Policy must be acknowledged before you can use this destination.";
      case "tosTestSucceeded":
        return "LinkedIn's Healthcare Ads Policy was acknowledged.";
      default:
        return "You must acknowledge LinkedIn's Healthcare Ads Policy before using this destination.";
    }
  }, [tosState]);

  const handleAccept = async () => {
    if (!existingConfig.id) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await makeRequest("acknowledge_linkedin_audiences_policy", {
        destinationID: existingConfig.id,
      });
      await refetchDestinations();
      setIsModalOpen(false);
      setIsChecked(false);
    } catch (err) {
      setError("Failed to save policy acknowledgment. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const button: React.ReactNode = useMemo(() => {
    if (tosState === "tosTestSucceeded") {
      return <Confirmation />;
    }

    return (
      <Button
        onClick={() => {
          setIsChecked(false);
          setError(null);
          setIsModalOpen(true);
        }}
      >
        Review & Accept Terms
      </Button>
    );
  }, [tosState]);

  return (
    <>
      <DestinationConfigRow
        name="LinkedIn Healthcare Ads Policy"
        disabled={!adAccountIdSet}
        textColor={textColor}
        labelText={labelText}
        labelStyles={{ maxWidth: 500, whiteSpace: "normal" }}
        onOpenConfigModal={() => {}}
        buttonComponent={button}
      />
      <Dialog
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="LinkedIn Healthcare Ads Policy"
        description={
          <p>
            To use LinkedIn Audiences with Freshpaint, LinkedIn requires that
            you agree to comply with LinkedIn's Healthcare Ads Policy available{" "}
            <a
              href="https://www.linkedin.com/help/lms/answer/a1320991"
              target="_blank"
              rel="noopener noreferrer"
              className="text-textActionSelected underline"
              onClick={handleOpenTOSLink}
            >
              here
            </a>
            . By checking the box below you confirm that you have read,
            understand and agree to comply with the LinkedIn Healthcare Ads
            policy and agree to not use Freshpaint in any way that would violate
            this policy.
          </p>
        }
      >
        <div className="mt-4 space-y-4">
          <label className="flex cursor-pointer items-start gap-3">
            <Checkbox
              disabled={!didOpenTOSLink}
              checked={isChecked}
              onCheckedChange={(checked) => setIsChecked(checked === true)}
              className="mt-0.5 flex-shrink-0"
            />
            <span className="text-sm">
              <strong>
                I have read and agree to abide by the LinkedIn Healthcare Ads
                Policy and agree not to use Freshpaint in a manner that violates
                this policy.
              </strong>
            </span>
          </label>

          {errorMessage && (
            <p className="text-textError text-sm">{errorMessage}</p>
          )}

          <DialogActionsWrapper className="mt-6">
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!isChecked || isSubmitting}
              leadingIcon={
                isSubmitting ? <LoadingSpinner className="size-4" /> : null
              }
            >
              Accept
            </Button>
          </DialogActionsWrapper>
        </div>
      </Dialog>
    </>
  );
};
